import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Form, Modal, Row, Tooltip, Upload, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import PhoneInput from 'react-phone-input-2';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { useSelector } from 'react-redux';

import {
  AddItemButton,
  CustomButton,
  CustomDynamicSelect,
  CustomInput,
  CustomTitle,
} from '../../../components';
import TeamSelectField from '../../../components/TeamSelectField';
import { useToken } from '../../../common/helper';
import { useSpin } from '../../../components/SpinContext';
import { AddUsers } from '../../../apis/onboarding';
import { GetRoles } from '../../../apis/roles';
import {
  downloadInviteTemplate,
  parseBulkInviteUsers,
  type BulkInviteUserRow,
} from '../../../apis/settings';
import { getTeamsDDLOFFeature } from '../../../common/utils/dropDown';
import type { TeamDDLOption } from '../../../apis/lookup';
import type { ErrorLike } from '@/apis/types';
import useUnauthorize from '../../../hooks/useUnauthorize';
import theme from '@/assets/theme';
import { DeleteIcon, MailIcon, MailOffIcon } from '@/assets/icons/icons';
import ImportUsersReviewModal from './ImportUsersReviewModal';

import './InviteUserModal.scss';

type ActiveTab = 'single' | 'bulk';
type BulkStatus = 'idle' | 'analyzing' | 'ready' | 'error';

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { token, decodedUserToken } = useToken();
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const { Dragger } = Upload;

  const [activeTab, setActiveTab] = useState<ActiveTab>('single');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkStatus, setBulkStatus] = useState<BulkStatus>('idle');
  const [bulkUsers, setBulkUsers] = useState<BulkInviteUserRow[]>([]);
  const [validCount, setValidCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);

  const teamsList = useSelector(
    (state: { teams: { list: Array<{ name: string }> } }) => state.teams.list
  );
  const hasValidTeams = !(
    teamsList.length === 0 ||
    (teamsList.length === 1 && teamsList[0].name.toLowerCase() === 'default')
  );

  const [allTeams, setAllTeams] = useState<TeamDDLOption[]>([]);
  const [teamsSearch, setTeamsSearch] = useState('');
  const [roleList, setRoleList] = useState<
    Array<{ id: string; name: string; disabled?: boolean }>
  >([]);

  const decodedToken = decodedUserToken();
  const isCompanyOwner = decodedToken?.is_company_owner === true;

  const resetState = useCallback(() => {
    form.resetFields();
    form.setFieldsValue({ teams: [{ team: undefined, role: undefined }] });
    setActiveTab('single');
    setSendEmail(true);
    setSubmitting(false);
    setIsFormValid(false);
    setBulkFile(null);
    setBulkStatus('idle');
    setBulkUsers([]);
    setValidCount(0);
    setErrorCount(0);
    setReviewOpen(false);
  }, [form]);

  useEffect(() => {
    if (open) {
      resetState();
    }
  }, [open, resetState]);

  const fetchTeams = useCallback(() => {
    const _ = getTeamsDDLOFFeature(token ?? '', 'Users', teamsSearch).then(
      (val) => {
        setAllTeams(val || []);
      }
    );
  }, [token, teamsSearch]);

  useEffect(() => {
    if (open) fetchTeams();
  }, [fetchTeams, open]);

  useEffect(() => {
    if (!open) return;
    GetRoles(token ?? '')
      .then((res) => {
        const data = res?.data;
        if (data?.data?.is_success) {
          setRoleList(data?.data?.data?.length > 0 ? data.data.data : []);
        }
      })
      .catch((error) => unauthorize(error));
  }, [open, token, unauthorize]);

  const teamLabel = theme.themeType === 2 ? 'Client + role' : 'Team + role';

  const handleClose = () => {
    resetState();
    onClose();
  };

  const onFinishSingle = (values: Record<string, unknown>) => {
    const teams =
      (values.teams as Array<{
        team?: { id?: string } | string;
        role?: string;
      }>) || [];
    const formattedTeams = teams.map((item) => ({
      team: typeof item.team === 'object' ? item.team?.id : item.team,
      role: item.role,
    }));

    const model = {
      ...values,
      teams: formattedTeams,
      id: null as number | null,
      send_email: sendEmail,
    };

    setSubmitting(true);
    setIsFormValid(true);
    showSpin();
    AddUsers(token ?? '', model, false)
      .then((res) => {
        const data = res?.data;
        if (data?.data?.is_success) {
          message.success(data?.data?.message || 'User invited');
          handleClose();
          onSuccess();
        } else {
          message.error(data?.data?.message || 'Invite failed');
        }
      })
      .catch((error) => unauthorize(error))
      .finally(() => {
        hideSpin();
        setSubmitting(false);
        setIsFormValid(false);
      });
  };

  const handleDownloadTemplate = async () => {
    try {
      const { blob, filename } = await downloadInviteTemplate(token ?? '');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      unauthorize(error as ErrorLike);
      message.error('Could not download template');
    }
  };

  const analyzeFile = async (file: File) => {
    setBulkFile(file);
    setBulkStatus('analyzing');
    setBulkUsers([]);
    setValidCount(0);
    setErrorCount(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await parseBulkInviteUsers(token ?? '', formData);
      // axios response: res.data = { data: { is_success, data, message } }
      const envelope = (res as { data?: { data?: Record<string, unknown> } })
        ?.data?.data as
        | {
            is_success?: boolean;
            message?: string;
            data?: {
              users?: BulkInviteUserRow[];
              valid_count?: number;
            };
          }
        | undefined;

      if (envelope?.is_success && envelope?.data) {
        const users = (envelope.data.users || []).map((u, idx) => ({
          ...u,
          send_email: u.send_email !== false,
          key: `${u.row}-${idx}-${u.email}`,
        }));
        setBulkUsers(users);
        setValidCount(envelope.data.valid_count || 0);
        setErrorCount(
          (envelope.data as Record<string, unknown> & { error_count?: number })
            .error_count || 0
        );
        setBulkStatus('ready');
      } else {
        setBulkStatus('error');
        message.error(envelope?.message || 'Could not analyze spreadsheet');
      }
    } catch (error) {
      setBulkStatus('error');
      unauthorize(error as ErrorLike);
      message.error('Could not analyze spreadsheet');
    }
  };

  const handleBulkPrimary = () => {
    if (bulkStatus === 'ready' && bulkUsers.length > 0) {
      setReviewOpen(true);
    }
  };

  const clearBulkFile = () => {
    setBulkFile(null);
    setBulkStatus('idle');
    setBulkUsers([]);
    setValidCount(0);
    setErrorCount(0);
  };

  const singleForm = (
    <Form
      form={form}
      layout='vertical'
      onFinish={onFinishSingle}
      autoComplete='off'
      className='invite-user-modal__form'
      initialValues={{ teams: [{ team: undefined, role: undefined }] }}
    >
      <Form.Item
        className='custom-input form-input-style'
        label='Name'
        name='name'
        rules={[
          { required: true, message: 'Please enter name' },
          {
            type: 'string',
            min: 3,
            message: 'Name must be at least 3 characters long',
          },
        ]}
      >
        <CustomInput
          placeholder='Enter name'
          height='32px'
          width='100%'
          border='0px'
          bgColor='var(--light-gray)'
          textColor='var(--text-secondary)'
          borderRadius='8px'
          placeholderColor='var(--text-secondary)'
          lineHeight='32px'
        />
      </Form.Item>

      <Form.Item
        className='custom-input form-input-style'
        label='Title'
        name='title'
        rules={[
          { required: true, message: 'Please enter title' },
          {
            type: 'string',
            min: 2,
            message: 'Title must be at least 2 characters long',
          },
        ]}
      >
        <CustomInput
          placeholder='Enter title'
          height='32px'
          width='100%'
          border='0px'
          bgColor='var(--light-gray)'
          textColor='var(--text-secondary)'
          borderRadius='8px'
          placeholderColor='var(--text-secondary)'
          lineHeight='32px'
        />
      </Form.Item>

      <Form.Item
        className='custom-input form-input-style'
        label='Email'
        name='email'
        rules={[
          { required: true, message: 'Please enter your email' },
          { type: 'email', message: 'Enter valid email' },
        ]}
      >
        <CustomInput
          placeholder='Enter email'
          height='32px'
          width='100%'
          border='0px'
          bgColor='var(--light-gray)'
          textColor='var(--text-secondary)'
          borderRadius='8px'
          placeholderColor='var(--text-secondary)'
          lineHeight='32px'
        />
      </Form.Item>

      <Form.Item
        className='custom-input form-input-style'
        label='Phone number (optional)'
        name='phone_number'
        rules={[
          {
            validator: (_: unknown, value: string) => {
              if (!value || String(value).trim() === '')
                return Promise.resolve();
              try {
                // Match backend / bulk review: format+length check with US default,
                // so normalized stored values (and bare US digits) don't fail on edit.
                if (isPossiblePhoneNumber(value, 'US'))
                  return Promise.resolve();
                return Promise.reject(new Error('Invalid phone number'));
              } catch {
                return Promise.reject(new Error('Invalid phone number'));
              }
            },
          },
        ]}
      >
        <PhoneInput
          country='us'
          value={form.getFieldValue('phone_number')}
          onChange={(_value, _countryData, _e, formattedValue) => {
            form.setFieldsValue({ phone_number: formattedValue });
          }}
        />
      </Form.Item>

      <Form.List name='teams'>
        {(fields, { add, remove }) => (
          <>
            <div className='invite-user-modal__section-label'>
              {hasValidTeams ? teamLabel : 'Role'}{' '}
              <span className='invite-user-modal__required'>*</span>
            </div>
            <Row gutter={[8, 8]}>
              {fields.map(({ key, name, ...restField }, index) => {
                const selectedTeamIds = form
                  .getFieldValue('teams')
                  ?.map(
                    (field: { team?: { id?: string } }, i: number) =>
                      i !== index && field?.team?.id
                  )
                  ?.filter(Boolean);

                const teamValue = form.getFieldValue(['teams', name, 'team']);
                const isAlreadySelected = selectedTeamIds?.includes(
                  teamValue?.id
                );
                const isDisabled =
                  (teamValue?.name?.toLowerCase() === 'default' &&
                    isCompanyOwner &&
                    teamValue?.role_name?.toLowerCase() === 'admin') ||
                  isAlreadySelected;

                return (
                  <React.Fragment key={key}>
                    {hasValidTeams && (
                      <Col span={11}>
                        <Form.Item
                          {...restField}
                          name={[name, 'team']}
                          rules={[
                            {
                              required: true,
                              message: `Select a ${
                                theme.themeType === 2 ? 'client' : 'team'
                              }`,
                            },
                          ]}
                          style={{ marginBottom: 0 }}
                        >
                          <TeamSelectField
                            name={String(name)}
                            allTeams={allTeams}
                            isEdit={false}
                            onTeamChange={() => undefined}
                            selectedTeamIds={selectedTeamIds}
                            onSearch={setTeamsSearch}
                            teamsSearch={teamsSearch}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    <Col span={hasValidTeams ? 11 : 22}>
                      <Form.Item
                        {...restField}
                        name={[name, 'role']}
                        rules={[{ required: true, message: 'Select a role' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <CustomDynamicSelect
                          className='form-input-style'
                          placeholder='Select'
                          disabled={!!isDisabled}
                          width='100%'
                          height='32px'
                          border='0px'
                          bgColor='var(--light-gray)'
                          textColor='var(--text-secondary)'
                          borderRadius='8px'
                          options={roleList.map((role) => ({
                            ...role,
                            label: role.name,
                            value: role.id,
                          }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      span={2}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {fields.length > 0 && (
                        <button
                          type='button'
                          className={`invite-user-modal__remove-btn${hasValidTeams ? '' : ' invite-user-modal__remove-btn--disabled'}`}
                          aria-label='Remove'
                          aria-disabled={!hasValidTeams}
                          onClick={() => {
                            if (hasValidTeams) remove(name);
                          }}
                        >
                          <DeleteIcon color='var(--text-primary)' />
                        </button>
                      )}
                    </Col>
                  </React.Fragment>
                );
              })}
            </Row>
            {hasValidTeams && (
              <div className='invite-user-modal__add-team'>
                <AddItemButton
                  marginTop='0px'
                  fontSize='12px'
                  fontWeight='600'
                  text=' Assign team + role'
                  onClick={() => add()}
                />
              </div>
            )}
          </>
        )}
      </Form.List>

      <p className='invite-user-text'>
        Teams control what content and data a user sees. Roles control what a
        user can do. <Link to='/settings/roles'>Learn more</Link>
      </p>

      <div className='invite-user-modal__actions'>
        <CustomButton
          text='Invite'
          width='100%'
          height='40px'
          borderRadius='8px'
          bgColor={isFormValid || submitting ? '#F3F3F3' : '#000'}
          color={isFormValid || submitting ? '#A6A6A6' : '#fff'}
          border='0px'
          fontSize='14px'
          fontWeight={600}
          htmlType='submit'
          isFormValid={isFormValid || submitting}
        />
        <button
          type='button'
          className='invite-user-modal__cancel'
          onClick={handleClose}
        >
          Cancel
        </button>
      </div>
    </Form>
  );

  const bulkTab = (
    <div className='invite-user-modal__bulk'>
      <div className='invite-user-modal__upload-label'>Upload spreadsheet</div>
      <Dragger
        multiple={false}
        showUploadList={false}
        accept='.csv,.xlsx'
        beforeUpload={(file) => {
          const name = file.name.toLowerCase();
          const ok = ['.csv', '.xlsx'].some((ext) => name.endsWith(ext));
          if (!ok) {
            message.error(`Unsupported file type: ${file.name}`);
            return false;
          }
          if (file.size > 5 * 1024 * 1024) {
            message.error('File exceeds 5MB.');
            return false;
          }
          const _ = analyzeFile(file);
          return false;
        }}
        className='invite-user-modal__dragger'
      >
        <p className='invite-user-modal__drop-text'>
          Drop files here to upload...
        </p>
        <button type='button' className='invite-user-modal__browse'>
          Browse files
        </button>
      </Dragger>

      <p className='invite-user-modal__template-hint'>
        Download this{' '}
        <button
          type='button'
          className='invite-user-modal__template-link'
          onClick={handleDownloadTemplate}
        >
          template
        </button>{' '}
        to fill in and upload
      </p>

      {bulkFile && (
        <div className='invite-user-modal__file-card'>
          <div className='invite-user-modal__file-row'>
            <span className='invite-user-modal__file-name'>
              {bulkFile.name}
            </span>
            <button
              type='button'
              className='invite-user-modal__file-remove'
              onClick={clearBulkFile}
              aria-label='Remove file'
            >
              <CloseOutlined style={{ fontSize: 12 }} />
            </button>
          </div>
          <p
            className={
              bulkStatus === 'ready'
                ? 'invite-user-modal__file-status invite-user-modal__file-status--success'
                : bulkStatus === 'error'
                  ? 'invite-user-modal__file-status invite-user-modal__file-status--error'
                  : 'invite-user-modal__file-status'
            }
          >
            {validCount > 0 && (
              <span>
                {validCount} user{validCount === 1 ? '' : 's'} found
              </span>
            )}
            {bulkStatus === 'analyzing' && (
              <span>Analyzing spreadsheet...</span>
            )}
            {bulkStatus === 'error' && (
              <span>Could not analyze spreadsheet</span>
            )}
            {errorCount > 0 && bulkStatus === 'ready' && (
              <span className='invite-user-modal__file-status-issues'>
                {validCount > 0 ? ', ' : ''}
                {errorCount} with issues
              </span>
            )}
          </p>
        </div>
      )}

      <div className='invite-user-modal__actions'>
        <CustomButton
          text={bulkStatus === 'ready' ? 'Invite' : 'Continue'}
          width='100%'
          height='40px'
          borderRadius='8px'
          bgColor={bulkStatus === 'ready' ? '#000' : '#F3F3F3'}
          color={bulkStatus === 'ready' ? '#fff' : '#A6A6A6'}
          border='0px'
          fontSize='14px'
          fontWeight={600}
          onClick={handleBulkPrimary}
          isFormValid={bulkStatus !== 'ready'}
        />
        <button
          type='button'
          className='invite-user-modal__cancel'
          onClick={handleClose}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        footer={null}
        width={480}
        centered
        destroyOnHidden
        className='invite-user-modal'
        closable={false}
        maskClosable={false}
        styles={{
          body: { padding: 0 },
        }}
      >
        <div className='invite-user-modal__header'>
          <CustomTitle
            text='Invite user'
            fontSize={20}
            fontWeight='700'
            lineHeight='28px'
            color='var(--text-primary)'
            marginBottom={0}
          />
          <div className='invite-user-modal__header-actions'>
            {activeTab === 'single' && (
              <Tooltip
                title={`Send 'Set password' email: ${sendEmail ? 'ON' : 'OFF'}`}
              >
                <button
                  type='button'
                  className='invite-user-modal__icon-btn'
                  onClick={() => setSendEmail((v) => !v)}
                  aria-label='Toggle send email'
                >
                  {sendEmail ? (
                    <MailIcon
                      width={16}
                      height={16}
                      color='var(--text-primary)'
                    />
                  ) : (
                    <MailOffIcon width={16} height={16} color='var(--danger)' />
                  )}
                </button>
              </Tooltip>
            )}
            <button
              type='button'
              className='invite-user-modal__icon-btn'
              onClick={handleClose}
              aria-label='Close'
            >
              <CloseOutlined />
            </button>
          </div>
        </div>

        <div className='invite-user-modal__tabs' role='tablist'>
          <button
            type='button'
            role='tab'
            aria-selected={activeTab === 'single'}
            className={
              activeTab === 'single'
                ? 'invite-user-modal__tab invite-user-modal__tab--active'
                : 'invite-user-modal__tab'
            }
            onClick={() => setActiveTab('single')}
          >
            Single invite
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={activeTab === 'bulk'}
            className={
              activeTab === 'bulk'
                ? 'invite-user-modal__tab invite-user-modal__tab--active'
                : 'invite-user-modal__tab'
            }
            onClick={() => setActiveTab('bulk')}
          >
            Bulk import
          </button>
        </div>

        <div className='invite-user-modal__body'>
          {activeTab === 'single' ? singleForm : bulkTab}
        </div>
      </Modal>

      <ImportUsersReviewModal
        open={reviewOpen}
        users={bulkUsers}
        onUsersChange={setBulkUsers}
        onClose={() => setReviewOpen(false)}
        onSuccess={(count) => {
          setReviewOpen(false);
          handleClose();
          onSuccess();
          message.success({
            content: (
              <span>
                {count} user{count === 1 ? '' : 's'} invited from spreadsheet
              </span>
            ),
            icon: (
              <span className='invite-success-banner__icon'>
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                  <circle cx='8' cy='8' r='8' fill='var(--green-900)' />
                  <path
                    d='M4.5 8.2L6.8 10.5L11.5 5.5'
                    stroke='white'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </span>
            ),
            className: 'invite-success-banner',
          });
        }}
        allTeams={allTeams}
        roleList={roleList}
        onRefresh={onSuccess}
      />
    </>
  );
};

export default InviteUserModal;
